import React, { useState } from 'react';
import Button from '../Button';
import Callout from '../Callout';
import PDFViewer from './PDFViewer';
import axios from 'axios'; // Added axios import

const EnrollmentContract = () => {
  const [step, setStep] = useState(1); // 1: Adopt, 2: Place, 3: Final
  const [signature, setSignature] = useState('');
  const [initials, setInitials] = useState('');
  const [placedSignatures, setPlacedSignatures] = useState({});
  const [signed, setSigned] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleAdoptSignatures = () => {
    if (!signature || !initials) {
      alert('Please provide both your full name and initials.');
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePlaceSignature = (fieldId) => {
    setPlacedSignatures(prev => ({
      ...prev,
      [fieldId]: { 
        value: fieldId.includes('initials') ? initials : 
               fieldId === 'parent_name' ? signature : 
               fieldId === 'parent_date' ? new Date().toLocaleDateString() : signature 
      }
    }));
  };

  const handleFieldClick = (field) => {
    setSelectedField(field);
    handlePlaceSignature(field.id);
  };

  const handleContinueToFinal = async () => {
    setIsGeneratingPdf(true);
    try {
      const success = await generateAndSavePDF();
      if (success) {
        setStep(3);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert('Failed to generate PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const generateAndSavePDF = async () => {
    try {
      const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
      const token = localStorage.getItem('token');
      
      // Prepare contract data
      const contractData = {
        placedSignatures: placedSignatures,
        userInfo: {
          name: `${localStorage.getItem('firstName')} ${localStorage.getItem('lastName')}`,
          email: localStorage.getItem('email')
        },
        completionDate: new Date().toISOString()
      };
      
      // Send to backend for PDF generation
      const response = await axios.post(
        `${API_ENDPOINT}/api/generate-enrollment-contract-pdf/`,
        { contractData },
        { headers: { Authorization: `Token ${token}` } }
      );
      
      if (response.data.success) {
        // Construct full PDF URL from backend response
        const pdfUrl = response.data.pdf_url;
        console.log('Backend returned PDF URL:', pdfUrl);
        if (pdfUrl) {
          // If it's a relative URL, make it absolute
          const fullPdfUrl = pdfUrl.startsWith('http') 
            ? pdfUrl 
            : `${API_ENDPOINT}${pdfUrl}`;
          console.log('Constructed full PDF URL:', fullPdfUrl);
          
          // Fetch the PDF as a blob and create a blob URL (like parent form)
          try {
            const blob = await fetch(fullPdfUrl).then((res) => res.blob());
            const blobUrl = URL.createObjectURL(blob);
            console.log('Created blob URL:', blobUrl);
            setPdfUrl(blobUrl);
          } catch (fetchError) {
            console.error('Error fetching PDF as blob:', fetchError);
            // Fallback to direct URL
            setPdfUrl(fullPdfUrl);
          }
        }
        return true;
      } else {
        console.error('PDF generation failed:', response.data.error);
        return false;
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      return false;
    }
  };

  const handleFinalSign = async () => {
    const requiredFields = ['ferpa_initials', 'coppa_initials', 'tech_initials', 'academic_initials', 'enrollment_initials', 'liability_initials', 'dispute_initials', 'agreement_initials', 'parent_signature', 'parent_name', 'parent_date'];
    const placedFields = Object.keys(placedSignatures);
    const missingFields = requiredFields.filter(f => !placedFields.includes(f));
    
    if (missingFields.length > 0) {
      alert(`Please place all required signatures and initials. Missing: ${missingFields.join(', ')}`);
      return;
    }
    
    setIsSigning(true);
    
    try {
      // Update application status (PDF was already generated)
      const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_ENDPOINT}/api/enrollment-contract-completion/`, {}, {
        headers: { Authorization: `Token ${token}` }
      });
      
      // Update cached user data
      const cachedUser = JSON.parse(localStorage.getItem('cachedUser') || '{}');
      cachedUser.application_status = 'enrolled';
      localStorage.setItem('cachedUser', JSON.stringify(cachedUser));
      
      // Navigate to success page
      window.location.href = '/enrollment-success';
    } catch (error) {
      console.error('Error completing enrollment:', error);
      // Still navigate to success page even if there's an error
      window.location.href = '/enrollment-success';
    }
  };

  const renderSignatureField = (fieldId, label, type) => {
    const isPlaced = placedSignatures[fieldId];
    
    if (isPlaced) {
      return (
        <span
          style={{
            fontFamily: fieldId === 'parent_name' || fieldId === 'parent_date' ? 'inherit' : 'Times New Roman, serif',
            fontStyle: fieldId === 'parent_name' || fieldId === 'parent_date' ? 'normal' : 'italic',
            fontSize: '14px',
            color: '#000',
            fontWeight: 'normal'
          }}
        >
          {isPlaced.value}
        </span>
      );
    }

    return (
      <span
        onClick={() => handlePlaceSignature(fieldId)}
        style={{
          background: '#FFEB3B',
          padding: '2px 8px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 'bold',
          border: '2px dashed #333',
          color: '#333',
          display: 'inline-block',
          marginLeft: '5px'
        }}
        title={`Click to place ${type}`}
      >
        {label}
      </span>
    );
  };

  const renderContractText = () => (
    <div style={{ lineHeight: '1.6', fontSize: '15px' }}>
      <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '20px' }}>
        DUAL SCHOOL STUDENT PARTICIPATION AGREEMENT & ENROLLMENT CONTRACT
      </div>
      <div style={{ fontStyle: 'italic', marginBottom: '20px' }}>
        (Dual School is a California-Based Online High School maximizing Community College Dual Enrollment)
      </div>

      <p>This Student Participation Agreement ("Agreement") is entered into by and among:</p>
      <p>Dual School, a private online secondary education program operating in the State of California ("School");</p>
      <p>The Parent(s) or Legal Guardian(s) of the student ("Parent(s)" or "Guardian(s)");</p>
      <p>on this {new Date().getDate()} day of {new Date().toLocaleDateString('en-US', { month: 'long' })}, {new Date().getFullYear()}.</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 1: PROGRAM STRUCTURE
      </div>
      <p>Dual School offers a hybrid model of private secondary education that combines:</p>
      <ul>
        <li>Accredited virtual high school instruction from high-school partners ("Partner Institutions"),</li>
        <li>Dual or concurrent enrollment in courses offered by California Community Colleges and other postsecondary institutions ("Partner Institutions"),</li>
        <li>College readiness programming, pathway planning, and academic coaching.</li>
      </ul>

      <p>Dual School provides academic support, coaching, and accountability for high school students enrolled in college-level courses via college partners. The student is responsible for all academic engagement and coursework completion. While Dual School provides mentorship and structure to support student success, the Student is ultimately responsible for academic outcomes.</p>
      <p>Students are subject to both Dual School policies and the academic, behavioral, and attendance policies of Partner Institutions.</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 2: LEGAL COMPLIANCE
      </div>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        2.1 FERPA
      </div>
      <p>Pursuant to the Family Educational Rights and Privacy Act (FERPA) (20 U.S.C. § 1232g), Parent(s)/Guardian(s) acknowledge the following:</p>
      <ul>
        <li>Education records are confidential and protected.</li>
        <li>By signing this Agreement, Parent(s)/Guardian(s) authorize Dual School to receive and share academic records, attendance data, and disciplinary information with Partner Institutions as needed.</li>
        <li>Parents of students under 18 retain FERPA rights unless the student is enrolled in postsecondary coursework only.</li>
      </ul>
      <p>Parent/Guardian Initials: {renderSignatureField('ferpa_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        2.2 COPPA & California Privacy
      </div>
      <p>In accordance with the Children's Online Privacy Protection Act (COPPA) and the California Consumer Privacy Act (CCPA):</p>
      <ul>
        <li>Parent(s)/Guardian(s) consent to the collection and appropriate use of Student's personal information by school-related platforms (e.g., Canvas, Zoom, Google Classroom).</li>
        <li>Dual School does not sell or share student data for commercial purposes.</li>
      </ul>
      <p>Parent/Guardian Initials: {renderSignatureField('coppa_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 3: CODE OF CONDUCT & TECHNOLOGY USE
      </div>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        3.1 Code of Conduct
      </div>
      <ul>
        <li>Students must uphold standards of integrity, responsibility, and respectful conduct.</li>
        <li>Harassment, discrimination, academic dishonesty, and disruptive behavior are grounds for disciplinary action.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        3.2 Technology Use
      </div>
      <ul>
        <li>School technology and accounts are for academic purposes only.</li>
        <li>Students must protect login credentials and refrain from accessing or distributing harmful content.</li>
        <li>Students are responsible for maintaining access to working devices and a stable internet connection.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        3.3 Technology Purchase and Proctoring
      </div>
      <ul>
        <li>Students are required to purchase technology for their own personal participation in Dual School. The cost of this technology is not included in the tuition paid for Dual School.</li>
        <li>Partner Institutions may require certain technology requirements for test proctoring, such as video and audio capability. Students are required to procure their own technology to meet the proctoring requirements of Partner Institutions if necessary.</li>
      </ul>
      <p>Parent/Guardian Initials: {renderSignatureField('tech_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 4: ACADEMIC RESPONSIBILITIES & STANDARDS SPECIFIC TO COLLEGE LEVEL PARTNER INSTITUTIONS
      </div>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.1 College Grades Are Permanent
      </div>
      <ul>
        <li>All grades earned through Partner Institutions are permanent and become part of the Student's official college transcript.</li>
        <li>These grades could impact future college admission, scholarships, and financial aid.</li>
        <li>Students are responsible for reporting all college coursework on future applications.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.2 College Conduct Standards Apply
      </div>
      <ul>
        <li>Students must comply with the disciplinary policies of the college where enrolled.</li>
        <li>Violations (e.g., plagiarism, cheating) may result in expulsion or record holds beyond the control of Dual School.</li>
        <li>Colleges may impose stricter policies than those imposed by Dual School.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.3 Deadlines and Withdrawals
      </div>
      <ul>
        <li>Instructors in college level courses do not accept late work.</li>
        <li>Students must initiate a drop by the published college deadline. If not, the grade will appear on their permanent college transcript.</li>
        <li>Neither instructors nor Dual School are responsible for drops due to poor performance.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.4 Attendance and Oversight
      </div>
      <ul>
        <li>Colleges do not track daily attendance in the same way as high schools.</li>
        <li>Parents will not be notified of absences, missed assignments, or class cancellations.</li>
        <li>Dual School does not provide in-class supervision. In no case will Dual School be liable for Student actions taken as a result of lack of supervision.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.5 Participation Expectations
      </div>
      <ul>
        <li>Students may be required to participate in group projects, presentations, or field trips without accommodation for high school age.</li>
        <li>These requirements are non-negotiable.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.6 Instructor Authority
      </div>
      <ul>
        <li>Instructors may remove a student from a course for poor performance or behavior.</li>
        <li>Dual School cannot override an instructor's decision.</li>
        <li>Students must independently manage their time and academic performance and standing.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        4.7 Role of Parent or Guardian and Role of Dual School
      </div>
      <ul>
        <li>The Partner Institution assumes that both the Parent and Dual School's responsibilities end once a student is enrolled in college level coursework with a Partner Institution. It is then the Student's responsibility to complete the coursework in a timely manner pursuant to the rules and timeline of each specific course.</li>
        <li>After enrollment, Dual School has no authority under this section to advocate for the student in these matters. The rules will be enforced solely by the Partner Institution.</li>
      </ul>
      <p>Parent/Guardian Initials: {renderSignatureField('academic_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 5: ENROLLMENT TERMS
      </div>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        5.1 Annual Tuition
      </div>
      <ul>
        <li>Tuition covers coordination and pathway development that is best for each individual student, including enrollment in Partner Institutions for high school level coursework, college level coursework, and AI tutor support.</li>
        <li>Tuition is all-inclusive for California residents, including payments to Partner Institutions where applicable, with the exception of personal student technology. Nonresidents of California may incur additional fees for additional tuition to Partner Institutions. </li>
        <li>Tuition includes year-round academic coordination, pathway coaching, check-ins, and college readiness programming.</li>
        <li>Tuition cost is outlined in Attachment A.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        5.2 Withdrawal
      </div>
      <ul>
        <li>Parent(s)/Guardian(s) may voluntarily withdraw the Student at any time with written notice.</li>
        <li>Refunds follow the policy outlined in Attachment A.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        5.3 Termination
      </div>
      <ul>
        <li>The School may terminate a student's enrollment for:</li>
        <ul>
          <li>Academic misconduct;</li>
          <li>Disciplinary infractions;</li>
          <li>Consistent non-participation or failure to meet deadlines;</li>
          <li>Non-payment of tuition.</li>
        </ul>
      </ul>
      <p>Parent/Guardian Initials: {renderSignatureField('enrollment_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 6: UC GUARANTEE AND LIMITATION OF LIABILITY
      </div>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        6.1 Overview of the Guarantee:
      </div>
      <ul>
        <li>A fundamental advantage of Dual School is that the programming is carefully designed to meet the requirements of the Transfer Agreement Guarantee ("TAG"), offered to guarantee admission to the Student's choice of the following University of California schools: UC Davis, UC Irvine, UC Merced, UC Riverside, UC Santa Barbara, or UC Santa Cruz.</li>
        <li>To take advantage of this guarantee, the parties acknowledge the following:</li>
        <ul>
          <li>Every requirement outlined in the UC-provided "TAG Matrix" must be followed exactly in order to receive the guarantee.</li>
          <li>At the time of this contract, the details of the TAG Matrix can be found here: https://admission.universityofcalifornia.edu/_assets/files/transfer-requirements/2025-26-tag-matrix-with-summary-of-changes.pdf</li>
          <li>While Dual School is responsible for determining the pathways and timeline necessary to meet all of the TAG Matrix requirements of the student's chosen UC school, it is the Student's responsibility to complete the coursework as provided by the Partner Institution on the timeline provided by Dual School.</li>
          <li>It is the Student's responsibility to obtain the grade point average specified in the TAG matrix for the chosen UC TAG school.</li>
        </ul>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        6.2 Dual School Financial Guarantee:
      </div>
      <ul>
        <li>Because Dual School does not receive "apportionment" from the State of California, Dual School is able to focus on the best interest of each and every one of our individual students without outside interference from governmental entities who can withhold financial support if their parameters are not met.</li>
        <li>While the TAG Program is available outside of Dual School, meeting the requirements of the TAG Program takes meticulous planning, which is why Dual School delivers value directly to the student.</li>
        <li>Dual School guarantees its claims financially. If a Student meets all obligations outlined in the above-referenced TAG Matrix, follows the pathways and timeline provided by Dual School, and still does not obtain admission to the student's chosen TAG UC School, then Dual School will refund all money paid to Dual School for tuition for the entire duration that the Student is enrolled at Dual School. Refunds will be without interest and will be reduced by direct costs paid to Partner Institutions for high school or college courses.</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        6.3 Limitation of Liability:
      </div>
      <ul>
        <li>Beyond this specific guarantee to the student's chosen TAG UC School as outlined in Section 6.2, Dual School makes no guarantees of:</li>
        <ul>
          <li>College admission,</li>
          <li>Financial aid or scholarship outcomes,</li>
          <li>Course credit transferability,</li>
          <li>Student success in coursework,</li>
          <li>The ongoing existence of Dual School.</li>
        </ul>
        <li>No liability will be assumed for the continuance of Dual School through any particular timeline, including any particular student's high school education years.</li>
        <li>Dual School disclaims liability for actions or omissions of Partner Institutions or third-party providers.</li>
      </ul>
      <p><strong>IN NO EVENT SHALL EITHER PARTY'S AGGREGATE CUMULATIVE LIABILITY HEREUNDER (WHETHER IN CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE) EXCEED THE TUITION AND FEES PAID OR PAYABLE BY CUSTOMER TO DUAL SCHOOL. IN NO EVENT SHALL DUAL SCHOOL, OR ITS PARENT COMPANY EDXCELERATE, BE LIABLE FOR ANY CONSEQUENTIAL, INCIDENTAL, INDIRECT, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES, LOSSES, OR EXPENSES.</strong></p>
      <p>Parent/Guardian Initials: {renderSignatureField('liability_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 7: DISPUTE RESOLUTION (CALIFORNIA)
      </div>
      <p>This Agreement is governed by the laws of the State of California.</p>
      <p>Disputes shall be resolved as follows:</p>
      <ul>
        <li>First through informal mediation facilitated by the School;</li>
        <li>If unresolved, through binding arbitration in El Dorado County, California;</li>
        <li>Legal claims may not proceed in court unless required by California Education Code § 56505 for special education matters.</li>
      </ul>
      <p>Parent/Guardian Initials: {renderSignatureField('dispute_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 8: ENTIRE AGREEMENT
      </div>
      <p>This Agreement:</p>
      <ul>
        <li>Supersedes all prior written or oral agreements;</li>
        <li>May only be amended by mutual written consent;</li>
        <li>Incorporates student policies via required initials.</li>
      </ul>
      <p>Registered as a private school with the California Department of Education through the Private School Affidavit process. Not accredited or endorsed by the State of California.</p>

      <p>Parent/Guardian Initials: {renderSignatureField('agreement_initials', 'Click to initial here', 'initials')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        SECTION 9: SIGNATURES
      </div>
      <p>By signing, parties agree to all terms of this Agreement, acknowledge the academic and behavioral expectations, and confirm they have read and initialed the required sections.</p>

      <p>Parent/Guardian Signature: {renderSignatureField('parent_signature', 'Click to sign here', 'signature')}</p>
      <p>Printed Name: {renderSignatureField('parent_name', 'Click to add name here', 'name')}</p>
      <p>Date: {renderSignatureField('parent_date', 'Click to add date here', 'date')}</p>

      <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '20px', marginBottom: '10px' }}>
        ATTACHMENTS
      </div>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        Attachment A – Tuition & Fee Schedule
      </div>
      <p><strong>Annual Tuition: $10,000</strong></p>
      <p>All-inclusive tuition covers:</p>
      <ul>
        <li>Year-round academic coaching</li>
        <li>Regular check-ins and college pathway planning</li>
        <li>Support with course enrollment and registration</li>
        <li>Assistance with pathway compliance for high school graduation requirements, A-G requirements, and UC TAG criteria through our pathway planner, EdXcelerate</li>
        <li>Payments for high school level coursework specified by Dual School, such as UC Scout, specifically in English and Math (please note that if a student takes outside coursework not specified as part of the Dual School pathway, it will not be covered by annual tuition)</li>
        <li>Payments for college level coursework specified by Dual School, including payments for textbooks if necessary (please note that if a student takes outside coursework not specified as part of the Dual School pathway, it will not be covered by annual tuition)</li>
        <li>Monthly payments for individual AI tutor through Khanmigo</li>
        <li>Attendance tracking through work completion required for California high school graduation</li>
        <li>Physical Education (PE) tracking required for California high school graduation</li>
        <li>"EdXcelerate Your Life" wraparound course to help with goal establishment and course completion and success</li>
        <li>Transcripts provided to colleges</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        Discounts:
      </div>
      <ul>
        <li>5% Upfront Payment Discount: $9,500 if paid in full before first day of enrollment</li>
        <li>5% Pod Discount: $9,500 per student for groups of 2+ enrolling together</li>
        <li>Stackable Total: $9,000 per student if both discounts apply</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        Payment Options:
      </div>
      <ul>
        <li>Option A: One-time payment of $9,500 if paid in full before first day of enrollment</li>
        <li>Option B: $1,000/month over 10 months (no discount - a late fee of 5% per month will apply on unpaid amounts)</li>
      </ul>
      
      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        Refund Policy:
      </div>
      <ul>
        <li>100% refund within 7 calendar days of enrollment, less direct payments already made on students behalf to Partner Institutions. For example, UC Scout high school level courses are $399 per student per semester. If Dual School has already paid $800 for the student to be enrolled in two semester-long courses, this amount will be deducted from the refund.</li>
        <li>50% refund within 30 calendar days, less direct payments already made on students behalf to Partner Institutions. For example, UC Scout high school level courses are $399 per student per semester. If Dual School has already paid $800 for the student to be enrolled in two semester-long courses, this amount will be deducted from the refund.</li>
        <li>No refunds after 30 days</li>
      </ul>

      <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '5px' }}>
        Attachment B – Directory Information Opt-Out Form
      </div>
      <p>Under FERPA, the following may be disclosed as "directory information" unless opted out:</p>
      <ul>
        <li>Student name, grade level, honors/awards, enrollment status</li>
      </ul>
      <p>To opt out of the disclosure of directory information, sign and return this form within 15 calendar days of signing the Participation Agreement.</p>
      <p>
        <a 
          href="/directory-opt-out-form" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          Click here to access the Directory Information Opt-Out Form
        </a>
      </p>
    </div>
  );

  const renderStep1 = () => (
    <div style={{ textAlign: 'left', marginTop: '40px' }}>

      <div style={{ marginBottom: '30px' }}>
        <label style={{ display: 'block', marginBottom: '10px' }}>
          <strong>Full Name (for signatures)</strong>
        </label>
        <input
          type="text"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="Enter your full name"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '16px',
            marginBottom: '20px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />

        <label style={{ display: 'block', marginBottom: '10px' }}>
          <strong>Initials (for initialing sections)</strong>
        </label>
        <input
          type="text"
          value={initials}
          onChange={(e) => setInitials(e.target.value)}
          placeholder="Enter your initials"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '16px',
            marginBottom: '20px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
      </div>

      <div style={{ marginBottom: '30px' }}>
        <label style={{ display: 'block', marginBottom: '10px' }}>
          <strong>Signature Preview</strong>
        </label>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '20px',
            marginBottom: '20px',
            minHeight: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Times New Roman, serif',
            fontStyle: 'italic',
            fontSize: '24px',
            backgroundColor: '#fafafa',
          }}
        >
          {signature || 'Your signature preview'}
        </div>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          <strong>Initials Preview</strong>
        </label>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '20px',
            marginBottom: '20px',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Times New Roman, serif',
            fontSize: '20px',
            backgroundColor: '#fafafa',
          }}
        >
          {initials || 'Your initials preview'}
        </div>
      </div>

      <div
        style={{
          padding: '20px',
          backgroundColor: '#f4f4f4',
          borderRadius: '8px',
          border: '1px solid #ddd',
        }}
      >
        <p style={{ fontSize: '14px', marginBottom: '20px' }}>
          By clicking "Adopt Signatures", you are creating electronic representations of your signature and initials that will be used throughout this document.
        </p>
        <Button
          onClick={handleAdoptSignatures}
          disabled={!signature || !initials}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: (!signature || !initials) ? 'not-allowed' : 'pointer',
          }}
        >
          Adopt Signature and Initials
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ textAlign: 'left', marginTop: '40px' }}>

      <div style={{ 
        maxHeight: 500, 
        overflowY: 'auto', 
        border: '1px solid #eee', 
        padding: 16, 
        marginBottom: 32, 
        background: '#fafbfc'
      }}>
        {renderContractText()}
      </div>

      <Button
        onClick={handleContinueToFinal}
        disabled={Object.keys(placedSignatures).length < 11 || isGeneratingPdf}
      >
        {isGeneratingPdf ? 'Generating PDF...' : 'Continue to Final Review'}
      </Button>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ textAlign: 'left', marginTop: '40px' }}>

      {pdfUrl ? (
        <PDFViewer 
          pdfUrl={pdfUrl} 
          title="Your Enrollment Contract PDF" 
        />
      ) : (
        <div style={{ 
          maxHeight: 500, 
          overflowY: 'auto', 
          border: '1px solid #eee', 
          padding: 16, 
          marginBottom: 32, 
          background: '#fafbfc'
        }}>
          {renderContractText()}
        </div>
      )}

      <div
        style={{
          padding: '20px',
          backgroundColor: '#f4f4f4',
          borderRadius: '8px',
          border: '1px solid #ddd',
        }}
      >
        <p style={{ fontSize: '14px', marginBottom: '20px' }}>
          {pdfUrl 
            ? "Please review the PDF above. By clicking 'Confirm and Submit', you are electronically signing this enrollment contract and agreeing to all terms and conditions outlined in the document."
            : "By clicking 'Complete Signing', you are electronically signing this enrollment contract and agreeing to all terms and conditions outlined above. This electronic signature has the same legal effect as a handwritten signature."
          }
        </p>
        <Button
          onClick={handleFinalSign}
          disabled={isSigning}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: isSigning ? 'not-allowed' : 'pointer',
          }}
        >
          {isSigning ? 'Submitting...' : (pdfUrl ? 'Confirm and Submit' : 'Complete Signing')}
        </Button>
      </div>
    </div>
  );

  const getTitle = () => {
    if (step === 1) return "Adopt Your Signature and Initials";
    if (step === 2) return "Place Your Signatures and Initials";
    if (step === 3) return "Final Review and Sign";
    return "Enrollment Contract";
  };

  const getCalloutText = () => {
    if (step === 1) {
      return "Please provide your full name and initials that will be used throughout the contract signing, then click 'Adopt Signature and Initials'.";
    } else if (step === 2) {
      return "Please carefully review the contract below and click on each of the highlighted areas to place your signature or initials.";
    } else if (step === 3) {
      return "Please review the generated PDF of your enrollment contract above, then click 'Confirm and Submit' to finalize your enrollment.";
    }
    return "";
  };

  return (
    <div className="page-container" style={{ maxWidth: 900, margin: '0 auto', background: '#fff', padding: 32, borderRadius: 8 }}>
      <h1>{getTitle()}</h1>
      <Callout text={getCalloutText()} />
      
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
};

export default EnrollmentContract; 